import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronRight, PhoneForwarded, PhoneMissed } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { ScreenHeading, SelectCard, StepScreen } from './ActivationPrimitives';
import { MobileActivation } from '../../hooks/useMobileActivation';
import { RULES, RuleId, ruleAvailable } from '../../data/mobileForwarding';
interface AnswerModeScreenProps {
  a: MobileActivation;
  onBack?: () => void;
  onLearnMoreConsent: () => void;
}

/** "When should Vexu answer?" — two exclusive modes, optional per-situation rules, consent. */
export function AnswerModeScreen({
  a,
  onBack,
  onLearnMoreConsent
}: AnswerModeScreenProps) {
  const {
    isLight
  } = useTheme();
  const conditionalRules = RULES.filter(r => r.id !== 'all');
  const canContinue = a.consent && (a.mode === 'all' || a.rules.length > 0);
  return <>
      <StepScreen stepKey="mode" onBack={onBack} footer={<Button fullWidth disabled={!canContinue} onClick={a.goToMethod} data-id="element-132" data-mp="15f3kzgfvoclx">
            Continue
          </Button>} data-id="element-131" data-mp="x33uutesxnqk">
        <ScreenHeading title="When should Vexu answer?" subtitle="Choose how you'd like your AI assistant to handle your calls. You can change this anytime." data-id="element-133" data-mp="1abarbac6rq4e" />

        <div className="mb-5 space-y-3" role="radiogroup" aria-label="When Vexu answers" data-id="element-134" data-mp="11f35rtkyfxdt">
          <SelectCard selected={a.mode === 'conditional'} onClick={() => a.setMode('conditional')} icon={PhoneMissed} title="Only when I can't answer" badge="Suggested" desc="Your phone rings normally. If you're busy, don't answer, or your phone is unreachable, Vexu takes the call." data-id="element-135" data-mp="mmrgoo5nz195" />
          <SelectCard selected={a.mode === 'all'} onClick={() => a.setMode('all')} icon={PhoneForwarded} title="Let Vexu answer every call" desc="All incoming calls go directly to your AI assistant. Your phone won't ring for those forwarded calls." data-id="element-136" data-mp="3q9knujs8f73" />
        </div>

        <AnimatePresence initial={false} data-id="element-137" data-mp="25kns8cklvy43">
          {a.mode === 'conditional' && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-138" data-mp="21leznb1ay6a4">
              <p className={`mb-2 text-[11px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-139" data-mp="2pbj0jan3vrxf">
                Vexu answers
              </p>
              <div className={`mb-6 divide-y rounded-2xl border ${isLight ? 'divide-slate-100 border-slate-200 bg-white' : 'divide-white/5 border-white/10 bg-white/5'}`} data-id="element-140" data-mp="2fbs7zei3vmzz">
                {conditionalRules.map(r => {
              const available = ruleAvailable(a.carrier, a.os, r.id as RuleId);
              const checked = a.rules.includes(r.id);
              return <button key={r.id} type="button" role="checkbox" aria-checked={checked} disabled={!available} onClick={() => a.toggleRule(r.id)} className="flex w-full items-center gap-3 px-4 py-3.5 text-left disabled:cursor-not-allowed" data-id="element-141" data-mp="ytm8x4r0y5su">
                      <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-colors ${!available ? isLight ? 'border-slate-200 bg-slate-50' : 'border-white/10' : checked ? 'border-vexu bg-vexu' : isLight ? 'border-slate-300' : 'border-white/25'}`} data-id="element-142" data-mp="1hhhh893varek">
                        {checked && available && <Check className="h-3 w-3 text-white" strokeWidth={3} data-id="element-143" data-mp="3t9mrb37i1yrw" />}
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-144" data-mp="1nizwruv87679">
                        <span className={`block text-sm font-semibold ${!available ? isLight ? 'text-slate-400' : 'text-zinc-600' : isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-145" data-mp="1il82wvb0ta0f">
                          {r.title}
                        </span>
                        <span className={`block text-xs ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-146" data-mp="1rn7mnvdf6s72">
                          {available ? r.desc : 'Not available with your carrier'}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </motion.div>}
        </AnimatePresence>

        {!a.isReturning && <div className={`rounded-xl border p-4 ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/10 bg-white/5'}`} data-id="element-147" data-mp="18tbtio3hhxgw">
            <div className="flex items-start gap-3" data-id="element-148" data-mp="g70uvizfu6mq">
              <button type="button" role="checkbox" aria-checked={a.consent} aria-label="Consent to AI call handling terms" onClick={() => a.setConsent(!a.consent)} className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors ${a.consent ? 'border-vexu bg-vexu' : isLight ? 'border-slate-300 bg-white' : 'border-white/30'}`} data-id="element-149" data-mp="g67ur0uzifdk">
                {a.consent && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} data-id="element-150" data-mp="3bili6vlnmaop" />}
              </button>
              <div className="min-w-0" data-id="element-151" data-mp="3fj5gw3lvlmcy">
                <p className={`text-[13px] leading-[1.5] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-152" data-mp="31jw8hq3wvwjj">
                  I understand my AI assistant will identify itself to callers where required, calls may be
                  recorded or transcribed where legally required, and I authorize forwarding eligible calls to
                  Vexu. I can turn this off anytime through the app's forwarding setup.
                </p>
                <button type="button" onClick={onLearnMoreConsent} className={`mt-2 inline-flex items-center gap-0.5 text-[13px] font-medium underline ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-153" data-mp="1b0o3qtshvqb6">
                  Learn more
                  <ChevronRight className="h-3.5 w-3.5" data-id="element-154" data-mp="x99kqmyl7p44" />
                </button>
              </div>
            </div>
          </div>}
      </StepScreen>
    </>;
}